'use client'

import { X } from 'lucide-react'

import { RentalSortSelect } from '@/components/molecules/rental-sort-select.client'
import { itemWord, type RentalCategoryView, type RentalSort } from '@/lib/rental'
import { iconFor } from '@/lib/rental-icons'
import { PICKED_FILL } from '@/lib/surface'
import { META, MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * Everything you do to the grid, in one object that stays under your hand.
 *
 * ## Why the filters and the results strip merged
 *
 * They were two surfaces in two different materials — a bordered panel holding
 * the search and the pills, then a bare strip holding the count, the hire
 * length and the sort — stacked with 24px between them and reading as unrelated
 * furniture. Every control here narrows or reorders the same grid, so they are
 * one tool, and the page was drawing them as two.
 *
 * The length picker has since been taken out of the listing: it priced rather
 * than filtered, and it was the one control here that did not act on the grid's
 * membership or its order. Lengths are chosen on the item's own page, next to
 * the calendar and the ladder; the caption under this bar still names them.
 *
 * ## Why it sticks
 *
 * Measured before this change: at 1440×900 the first card began at 654px and
 * not one card was fully visible on the opening screen; on a 375×812 phone the
 * first card began at 842px — a full screen of chrome. Whatever else changes,
 * a reader who wants a different category after scrolling into the grid had to
 * travel back to the top to say so.
 *
 * It is a BAND, not a panel. The frame it used to wear — hairline, 24px
 * radius, drop shadow — put a box around a search field that was already a box.
 * What it keeps is the opaque fill and the blur, and those are not decoration:
 * a sticky bar spends its life over the grid rather than over the background,
 * so a translucent one would let photographs of cameras scroll through the
 * pills. The same reason `SURFACE_SOLID` was reached for; only the frame went.
 *
 * The offset clears the floating header — `top-3`/`sm:top-4` over a 50px bar,
 * so 62/66px — plus a hairline of air.
 */
export function RentalToolbar({
  categories,
  counts,
  active,
  onToggle,
  filtered,
  onReset,
  total,
  sort,
  onSort,
}: {
  categories: RentalCategoryView[]
  /** Category slug → how many items pressing it would show. */
  counts: Map<string, number>
  active: string[]
  onToggle: (slug: string) => void
  filtered: boolean
  onReset: () => void
  total: number
  sort: RentalSort
  onSort: (sort: RentalSort) => void
}) {
  return (
    <section
      aria-label="Filtry i sortowanie"
      // NO FRAME. It was a solid panel — hairline, 24px radius, drop shadow —
      // which drew a box around a search field that was already a box, and the
      // page then had a bordered card sitting on a bordered card.
      //
      // The fill and the blur STAY, and they are not decoration: this band is
      // sticky, so without an opaque ground the grid scrolls through it and the
      // pills end up sitting on photographs of cameras. The hairline underneath
      // is the band's own bottom edge, which is what a sticky bar needs to stop
      // looking glued to the content it is covering.
      className={cn(
        // The gap over the bar gives way with the window: it is the last thing
        // between the headline and the grid, and on a laptop it was 20px of
        // nothing above a band that is already 100px tall.
        'sticky top-[4.375rem] z-30 mt-[clamp(0.5rem,1.6svh,1.25rem)] sm:top-[4.625rem]',
        'border-b border-white/10 bg-[var(--flou-surface-solid)] backdrop-blur-xl',
      )}
    >
      {/* ONE ROW, ranged left. It used to be two — a search field on its own
          line and the pills under it, centred against an invisible mirror of
          the count so they sat on the bar's true middle. The field has moved
          to the top of the page (`RentalSearch`), which leaves the pills as
          the bar's content rather than its second half: ranged left under a
          left-ranged headline, with the count and the sort on the right, the
          mirror has nothing left to correct and the bar lost a row. */}
      <div className="flex items-center gap-2 px-3 py-2">
        {/* Bleeds to the bar's edge on the LEFT only. It used to bleed both
            ways, which was safe while the count lived in a separate strip —
            here the count is the next sibling, and a right-hand negative
            margin ran the scrolling pills underneath it. The cut-off pill that
            says "there is more to the right" survives: the row now ends
            against the count instead of against the bar's edge. */}
        <div
          className={cn(
            '-ml-3 flex min-w-0 flex-1 gap-2 overflow-x-auto pl-3',
            '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            // `flex-none` from `sm` so the row's own `justify-center` has
            // something to centre — a `flex-1` child fills the bar and centring
            // it moves nothing.
            'sm:ml-0 sm:flex-wrap sm:overflow-visible sm:pl-0',
          )}
        >
          {categories.map(({ slug, label }) => {
            const Icon = iconFor(slug)
            const isOn = active.includes(slug)
            const count = counts.get(slug) ?? 0
            return (
              <button
                key={slug}
                type="button"
                onClick={() => onToggle(slug)}
                aria-pressed={isOn}
                disabled={count === 0 && !isOn}
                className={cn(
                  META,
                  'inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 font-medium transition',
                  'disabled:cursor-not-allowed disabled:opacity-35',
                  // `PICKED_FILL`, not a fourth spelling of it. The pill wore
                  // its own copy of the gradient and its own dark ink; both
                  // now come from the token, which the owner has turned white
                  // everywhere — see `lib/surface`.
                  isOn
                    ? PICKED_FILL
                    : 'border-white/15 bg-white/[0.04] text-white/75 hover:border-white/35 hover:text-white',
                )}
              >
                <Icon size={14} aria-hidden="true" />
                {label}
                {/* Opaque on the pressed pill, not faded: a 55% dark over a
                    mid-luminance fill composites to 1.9:1. The size step is
                    what separates the count from the label here. */}
                <span className={cn(MICRO, isOn ? 'text-neutral-950' : 'text-white/55')}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>

        {filtered ? (
          <button
            type="button"
            onClick={onReset}
            className={cn(
              META,
              'inline-flex min-h-9 shrink-0 items-center gap-1 rounded-full px-2.5 font-medium text-white/60 transition-colors hover:text-white',
            )}
          >
            <X size={13} aria-hidden="true" />
            Wyczyść
          </button>
        ) : null}

        {/* The live region is the only signal a screen reader gets that the
            grid moved; the visible twin is the only one a sighted reader gets
            that typing narrowed anything. Both, not either. */}
        <p className="sr-only" aria-live="polite">
          {total} {itemWord(total)}
        </p>
        <p className={cn(META, 'shrink-0 font-semibold text-white')} aria-hidden="true">
          {total} {itemWord(total)}
        </p>

        <div className="shrink-0">
          <RentalSortSelect sort={sort} onSort={onSort} total={total} />
        </div>
      </div>
    </section>
  )
}
